/**
 * Browser-side guide processor: takes the raw ipep-user-guide.md text, annotates
 * each heading from the bundled GUIDE_HELP_MAP, and splits it into per-section
 * help entries (one per heading, content running to the next heading).
 *
 * Mirrors scripts/help-upload/annotate-guide.mjs + split-guide.mjs so the in-app
 * /help/import flow produces the same sections as the CLI pipeline.
 */

import { GUIDE_HELP_MAP, GuideHelpEntry, DOC_TITLE, buildMarker } from './guideHelpMap';

const HEADING_RE = /^(#{1,6})\s+(.+?)\s*$/;
const MARKER_RE = /^\s*<!--\s*help:.*-->\s*$/;
const IMAGE_REF_RE = /!\[[^\]]*\]\((images\/[^)]+)\)/g;

export type SectionKind = 'markdown' | 'attachment';

export interface GuideSection {
  num: number;
  id: string;
  title: string;
  level: number;
  moduleid: string;
  pageurl: string;
  datatestid: string;
  enabled: boolean;
  mapped: boolean;
  kind: SectionKind;
  /** Section markdown: heading + help marker + body (marker is an HTML comment). */
  content: string;
  /** Distinct `images/...` references found in this section, in order. */
  imageRefs: string[];
}

export interface ProcessResult {
  sections: GuideSection[];
  problems: string[];
}

function slugify(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50) || 'section'
  );
}

function collectImageRefs(markdown: string): string[] {
  const out: string[] = [];
  for (const m of markdown.matchAll(IMAGE_REF_RE)) {
    if (!out.includes(m[1])) out.push(m[1]);
  }
  return out;
}

function isAttachment(e: GuideHelpEntry): boolean {
  return e.moduleid === 'Global' && e.datatestid === 'help-document-download';
}

/**
 * Match headings to map entries by (level, title) using a FIFO queue per title,
 * so repeated titles ("Comment", "File acceptance", …) resolve in document order
 * regardless of small structural shifts.
 */
export function processGuide(markdown: string): ProcessResult {
  const lines = markdown.split('\n');
  const problems: string[] = [];

  const queue = new Map<string, GuideHelpEntry[]>();
  for (const e of GUIDE_HELP_MAP) {
    const key = `${e.level}::${e.title}`;
    const arr = queue.get(key);
    if (arr) arr.push(e);
    else queue.set(key, [e]);
  }

  interface Raw {
    level: number;
    title: string;
    entry: GuideHelpEntry | null;
    lines: string[];
  }
  const raws: Raw[] = [];
  let current: Raw | null = null;

  for (const line of lines) {
    const h = line.match(HEADING_RE);
    if (h) {
      const level = h[1].length;
      const title = h[2].trim();
      if (level === 1 && title === DOC_TITLE) {
        current = null; // document title is not a help section
        continue;
      }
      const key = `${level}::${title}`;
      const bucket = queue.get(key);
      const entry = bucket && bucket.length ? bucket.shift()! : null;
      if (!entry) problems.push(`Heading "${title}" (H${level}) is not in the help map — section will be skipped.`);
      current = { level, title, entry, lines: [] };
      raws.push(current);
      continue;
    }
    if (!current) continue;
    if (MARKER_RE.test(line)) continue; // drop pre-existing markers; we re-emit from the map
    current.lines.push(line);
  }

  for (const [key, bucket] of queue) {
    for (const e of bucket) {
      problems.push(`Help map entry "${e.title}" (${key}) was not found in the uploaded guide.`);
    }
  }

  const sections: GuideSection[] = raws.map((raw, i) => {
    const num = i + 1;
    const e = raw.entry;
    const moduleid = e?.moduleid ?? 'Global';
    const pageurl = e?.pageurl ?? '/global';
    const datatestid = e?.datatestid ?? '';

    const heading = `${'#'.repeat(raw.level)} ${raw.title}`;
    const headerLines = [heading];
    if (e) headerLines.push(buildMarker(e));
    headerLines.push('');
    const body = raw.lines.join('\n').replace(/\n+$/, '');
    const content = `${headerLines.join('\n')}\n${body}\n`.replace(/\n{3,}/g, '\n\n');

    const slug = slugify(raw.title);
    return {
      num,
      id: `guide-${String(num).padStart(2, '0')}-${slug}`,
      title: raw.title,
      level: raw.level,
      moduleid,
      pageurl,
      datatestid,
      enabled: Boolean(datatestid),
      mapped: Boolean(e),
      kind: e && isAttachment(e) ? 'attachment' : 'markdown',
      content,
      imageRefs: collectImageRefs(content),
    };
  });

  return { sections, problems };
}
